import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Megaphone, Loader2, Clock, Send, Zap, FlaskConical } from "lucide-react";

import BroadcastComposeForm from "@/components/broadcast/BroadcastComposeForm";
import BroadcastSegmentPicker from "@/components/broadcast/BroadcastSegmentPicker";
import BroadcastEmailPreview from "@/components/broadcast/BroadcastEmailPreview";
import BroadcastScheduleField, {
  defaultScheduledFor,
  fmtScheduled,
} from "@/components/broadcast/BroadcastScheduleField";
import BroadcastResultBanner from "@/components/broadcast/BroadcastResultBanner";
import ScheduledBroadcastList from "@/components/broadcast/ScheduledBroadcastList";
import BroadcastABFields from "@/components/broadcast/BroadcastABFields";

const LAUNCH_DEFAULT = {
  subject: "🎉 We're LIVE on mypandax.com — the web app is open!",
  heading: "We're live on the web. The vault is open.",
  body: `The wait is over — My Panda X is officially live at mypandax.com.

You can now:
🔴 Go live and host from any browser (co-hosts + battles supported)
🎁 Send gifts with 12 tiers from 🌹 Rose to 🐼 Panda jackpot
💬 DM anyone — even send gifts inside chat
🗺️ Find fellow pandas near you and worldwide
🎟️ Grab a Daily Golden Ticket for a shot at 5,000 coins

The mobile app drops next — you'll be the first to know.

Thank you for being here on day one. Let's kick this off tonight.`,
  cta_label: "Enter the Vault →",
  cta_url: (typeof window !== "undefined" ? window.location.origin : "") + "/feed",
};

/**
 * Admin broadcast composer — split into `broadcast/*` sub-components so
 * each concern lives in its own file:
 *   • BroadcastComposeForm     — subject/heading/body/CTA inputs
 *   • BroadcastSegmentPicker   — audience tile + desire tag + live count
 *   • BroadcastScheduleField   — datetime-local when mode=schedule
 *   • BroadcastResultBanner    — sent/queued/scheduled status line
 *   • ScheduledBroadcastList   — upcoming rows with cancel
 *
 * This shell owns state + API calls only, cutting the top-level component
 * complexity from 64 down to routine glue.
 */
export default function BroadcastModal({ open, onOpenChange }) {
  const [form, setForm] = useState(LAUNCH_DEFAULT);
  const [mode, setMode] = useState("now"); // "now" | "schedule" | "ab"
  const [scheduledFor, setScheduledFor] = useState(defaultScheduledFor());
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [scheduledList, setScheduledList] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [segment, setSegment] = useState("all");
  const [desireTag, setDesireTag] = useState("");
  const [segmentCount, setSegmentCount] = useState(null);
  const [countLoading, setCountLoading] = useState(false);
  // A/B specific state
  const [subjectB, setSubjectB] = useState("");
  const [testPercent, setTestPercent] = useState(5);
  const [testDurationMinutes, setTestDurationMinutes] = useState(30);
  const [config, setConfig] = useState(null);
  const [testBusy, setTestBusy] = useState(false);

  const loadScheduled = async () => {
    setLoadingList(true);
    try {
      const { data } = await api.get("/admin/broadcast/scheduled");
      setScheduledList(Array.isArray(data) ? data : []);
    } catch {
      /* silent — admin will see per-action toasts */
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    if (open) {
      setResult(null);
      loadScheduled();
      api.get("/admin/broadcast/config").then(({ data }) => setConfig(data)).catch(() => setConfig(null));
    }
  }, [open]);

  // Requirement: a stale campaign result must never linger over a new draft.
  useEffect(() => {
    if (!busy) setResult(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, segment, desireTag, mode, subjectB]);

  // Debounced segment count preview.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setCountLoading(true);
    const t = setTimeout(async () => {
      try {
        const params = { segment };
        if (segment === "desire") {
          const tag = desireTag.trim();
          if (!tag) {
            if (!cancelled) setSegmentCount(null);
            return;
          }
          params.desire_tag = tag;
        }
        const { data } = await api.get("/admin/broadcast/segment-count", { params });
        if (!cancelled) setSegmentCount(data.count);
      } catch {
        if (!cancelled) setSegmentCount(null);
      } finally {
        if (!cancelled) setCountLoading(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, segment, desireTag]);

  // Poll status for a queued immediate broadcast until "sent".
  const pollProgress = (id) => {
    let stopped = false;
    const tick = async () => {
      if (stopped) return;
      try {
        const { data } = await api.get(`/admin/broadcast/${id}`);
        setResult(data);
        if (data.status === "sent") {
          stopped = true;
          const bells = data.recipients_notified ?? data.recipients_dispatched;
          toast.success(`${bells}/${data.recipients_total} bells · ${data.recipients_dispatched} emails`);
          return;
        }
      } catch { /* keep polling */ }
      setTimeout(tick, 2500);
    };
    setTimeout(tick, 1500);
  };

  const submit = async () => {
    if (busy) return;
    if (segment === "desire" && !desireTag.trim()) {
      toast.error("Type a desire tag or pick a different segment.");
      return;
    }
    if (mode === "ab" && !subjectB.trim()) {
      toast.error("Enter a second subject line for variant B.");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      if (mode === "ab") {
        const payload = {
          subject_a: form.subject,
          subject_b: subjectB.trim(),
          heading: form.heading,
          body: form.body,
          cta_label: form.cta_label,
          cta_url: form.cta_url,
          segment,
          test_percent: testPercent,
          test_duration_minutes: testDurationMinutes,
        };
        if (segment === "desire") payload.desire_tag = desireTag.trim();
        const { data } = await api.post("/admin/ab-broadcast", payload);
        setResult({ ...data, ab: true });
        toast.success(`A/B queued — winner blasts in ${testDurationMinutes}m`);
        return;
      }
      const payload = { ...form, segment };
      if (segment === "desire") payload.desire_tag = desireTag.trim();
      if (mode === "schedule") {
        // Convert local datetime-local value → ISO with browser tz offset.
        payload.scheduled_for = new Date(scheduledFor).toISOString();
      }
      const { data } = await api.post("/admin/broadcast", payload);
      setResult(data);
      if (data.scheduled) {
        toast.success(`Scheduled for ${fmtScheduled(data.scheduled_for)}`);
        await loadScheduled();
      } else if (data.queued && data.broadcast_id) {
        toast.success("Queued — sending in the background.");
        pollProgress(data.broadcast_id);
      } else {
        const bells = data.recipients_notified ?? data.recipients_dispatched;
        toast.success(`${bells}/${data.recipients_total} bells · ${data.recipients_dispatched} emails`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Broadcast failed.");
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    if (testBusy) return;
    setTestBusy(true);
    try {
      const { data } = await api.post("/admin/broadcast/test", {
        subject: form.subject, heading: form.heading, body: form.body,
        cta_label: form.cta_label, cta_url: form.cta_url,
      });
      toast.success(`Test email sent to ${data.to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test email failed.");
    } finally {
      setTestBusy(false);
    }
  };

  const cancelScheduled = async (id) => {
    try {
      await api.delete(`/admin/broadcast/scheduled/${id}`);
      toast.success("Scheduled broadcast cancelled");
      await loadScheduled();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel.");
    }
  };

  const sendLabel = () => {
    if (busy) {
      if (mode === "schedule") return "Scheduling…";
      if (mode === "ab") return "Launching A/B…";
      return "Sending…";
    }
    if (mode === "schedule") return "Schedule broadcast";
    if (mode === "ab") return "Launch A/B test";
    if (segmentCount) return `Send to ${segmentCount.toLocaleString()} ${segmentCount === 1 ? "member" : "members"}`;
    return "Send";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass rounded-none max-w-5xl border-white/10 max-h-[92vh] overflow-y-auto" data-testid="broadcast-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Megaphone size={20} className="text-rose-500" />
            Broadcast to all members
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-sm">
            Send now or schedule an email blast. Pick a segment below to make it personal — creators, ambassadors, or everyone sharing a desire tag.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2">
          <ModePill val="now" label="Send now" Icon={Send} current={mode} onSelect={setMode} />
          <ModePill val="schedule" label="Schedule" Icon={Clock} current={mode} onSelect={setMode} />
          <ModePill val="ab" label="A/B test" Icon={Zap} current={mode} onSelect={setMode} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-3">
            <BroadcastComposeForm form={form} onChange={setForm} />

            <BroadcastSegmentPicker
              segment={segment}
              onSegmentChange={setSegment}
              desireTag={desireTag}
              onDesireTagChange={setDesireTag}
              countLoading={countLoading}
              segmentCount={segmentCount}
            />

            {mode === "schedule" && (
              <BroadcastScheduleField value={scheduledFor} onChange={setScheduledFor} />
            )}

            {mode === "ab" && (
              <BroadcastABFields
                subjectA={form.subject}
                subjectB={subjectB}
                onSubjectAChange={(v) => setForm((f) => ({ ...f, subject: v }))}
                onSubjectBChange={setSubjectB}
                testPercent={testPercent}
                onTestPercentChange={setTestPercent}
                testDurationMinutes={testDurationMinutes}
                onTestDurationChange={setTestDurationMinutes}
                audienceCount={segmentCount}
              />
            )}

            <BroadcastResultBanner result={result} />

            <div className="flex items-center justify-between pt-2 gap-3">
              <div className="text-xs text-zinc-500 min-w-0">
                Configured sender:{" "}
                <code className="text-white break-all" data-testid="broadcast-config-sender">
                  {config?.sender || "not configured"}
                </code>
                {config && !config.resend_configured && (
                  <span className="block text-rose-400 mt-0.5">Resend API key missing — emails will not send.</span>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  onClick={sendTest}
                  disabled={testBusy || busy}
                  variant="ghost"
                  className="rounded-none border border-white/15 text-xs px-4 py-5 hover:bg-white/5"
                  data-testid="broadcast-send-test-button"
                >
                  {testBusy ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <FlaskConical size={14} className="mr-1.5" />}
                  Send test email
                </Button>
                <Button
                  onClick={submit}
                  disabled={busy || segmentCount === 0}
                  className="btn-crimson rounded-none px-6 py-5"
                  data-testid="broadcast-send-button"
                >
                  {busy && <Loader2 size={16} className="animate-spin mr-2" />}
                  {sendLabel()}
                </Button>
              </div>
            </div>

            <ScheduledBroadcastList
              items={scheduledList}
              loading={loadingList}
              onCancel={cancelScheduled}
            />
          </div>

          <BroadcastEmailPreview
            form={form}
            segment={segment}
            desireTag={desireTag}
            segmentCount={segmentCount}
            sender={config?.sender}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Compact pill for the "Send now / Schedule" mode toggle. */
function ModePill({ val, label, Icon, current, onSelect }) {
  const active = current === val;
  return (
    <button
      type="button"
      onClick={() => onSelect(val)}
      data-testid={`broadcast-mode-${val}`}
      className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-xs uppercase tracking-widest border transition-colors ${
        active
          ? "bg-rose-600/20 border-rose-500 text-white"
          : "bg-black/30 border-white/10 text-zinc-400 hover:text-white"
      }`}
    >
      <Icon size={14} /> {label}
    </button>
  );
}
